import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import TopBar from '../components/TopBar';
import AircraftStatusReport from '../components/AircraftStatusReport';
import FlyAircraftModal from '../components/FlyAircraftModal';
import OnGroundMode from '../components/OnGroundMode';
import PostFlightMode from '../components/PostFlightMode';
import './DashboardPage.css';

const MenuCockpitIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M3.2 11.2 10 8.4l6.8 2.8-1 2.1-4.8-1.5v4.6H8.9v-4.6l-4.8 1.5-1-2.1Z" fill="currentColor" />
    <path d="M8.1 6.5h3.8l.7 1.2H7.4l.7-1.2Z" fill="currentColor" />
  </svg>
);

const MenuHistoricIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 3.3a6.7 6.7 0 1 1-6.1 9.4H2.4a.8.8 0 0 1-.7-1.2l1.8-3a.8.8 0 0 1 1.3 0l1.8 3a.8.8 0 0 1-.7 1.2H5.8A4.9 4.9 0 1 0 10 5.1c-1 0-2 .3-2.8.9l-1-1.4A6.6 6.6 0 0 1 10 3.3Z" fill="currentColor" />
    <path d="M9.1 6.7h1.7v3.1l2.3 1.4-.9 1.5-3.1-1.9V6.7Z" fill="currentColor" />
  </svg>
);

const MenuFolderIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M2.7 5.3c0-1 .8-1.8 1.8-1.8h3.7l1.5 1.8h5.8c1 0 1.8.8 1.8 1.8v6.6c0 1-.8 1.8-1.8 1.8H4.5c-1 0-1.8-.8-1.8-1.8V5.3Z" fill="currentColor" />
    <path d="M2.7 7h14.6v1.6H2.7z" fill="#16181b" opacity=".35" />
  </svg>
);

const MenuSettingsIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="m8.7 2.8 2.6.0.4 1.7c.4.1.8.3 1.1.5l1.6-.8 1.8 1.8-.8 1.6c.2.4.4.7.5 1.1l1.7.4v2.6l-1.7.4c-.1.4-.3.8-.5 1.1l.8 1.6-1.8 1.8-1.6-.8c-.4.2-.7.4-1.1.5l-.4 1.7H8.7l-.4-1.7c-.4-.1-.8-.3-1.1-.5l-1.6.8-1.8-1.8.8-1.6a5 5 0 0 1-.5-1.1l-1.7-.4V9l1.7-.4c.1-.4.3-.8.5-1.1l-.8-1.6 1.8-1.8 1.6.8c.4-.2.7-.4 1.1-.5l.4-1.7Z" fill="currentColor" />
    <circle cx="10" cy="10" r="2.4" fill="#16181b" opacity=".35" />
  </svg>
);

const MenuSignOutIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M7 3.5h4.3v1.8H7a1 1 0 0 0-1 1v7.4a1 1 0 0 0 1 1h4.3v1.8H7a2.8 2.8 0 0 1-2.8-2.8V6.3A2.8 2.8 0 0 1 7 3.5Z" fill="currentColor" />
    <path d="m11 6.1 1.3-1.3 5.2 5.2-5.2 5.2-1.3-1.3 2.9-2.9H8.2V8.9H14l-3-2.8Z" fill="currentColor" />
  </svg>
);

const DashboardPage = () => {
  const { aircraft, logout } = useAuth();
  const [showFlyModal, setShowFlyModal] = useState(false);
  const [activeFlight, setActiveFlight] = useState(null);
  const [activeFlightMode, setActiveFlightMode] = useState('on_ground');
  const [panelDirection, setPanelDirection] = useState('none');
  const [panelTransitionKey, setPanelTransitionKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState('cockpit');

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const menuItems = [
    { id: 'cockpit', label: 'Cockpit Mode', icon: MenuCockpitIcon },
    { id: 'historic', label: 'Historic A/C Data', icon: MenuHistoricIcon },
    { id: 'documents', label: 'Documents Folder', icon: MenuFolderIcon },
    { id: 'settings', label: 'Settings', icon: MenuSettingsIcon }
  ];

  const handleFlySuccess = (flight) => {
    setShowFlyModal(false);
    setActiveFlightMode(flight?.status === 'post_flight' ? 'post_flight' : 'on_ground');
    setPanelDirection('forward');
    setPanelTransitionKey(k => k + 1);
    setActiveFlight(flight);
  };

  const handleBackToAircraftReport = () => {
    setPanelDirection('back');
    setPanelTransitionKey(k => k + 1);
    setActiveFlight(null);
    setActiveFlightMode('on_ground');
  };

  const handleMoveToPostFlight = () => {
    setPanelDirection('forward');
    setPanelTransitionKey(k => k + 1);
    setActiveFlightMode('post_flight');
  };

  const handleReturnToOnGround = () => {
    setPanelDirection('back');
    setPanelTransitionKey(k => k + 1);
    setActiveFlightMode('on_ground');
  };

  return (
    <div className="dashboard-layout">
      <TopBar menuOpen={menuOpen} onMenuToggle={() => setMenuOpen(v => !v)} />
      <div className={`dashboard-menu-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)} />
      <aside className={`dashboard-menu-drawer ${menuOpen ? 'open' : ''}`} aria-hidden={!menuOpen}>
        <div className="dashboard-menu-head">
          <span className="dashboard-menu-title">Menu</span>
          <span className="dashboard-menu-head-icon" aria-hidden="true">≡</span>
        </div>

        <div className="dashboard-menu-items">
          {menuItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`dashboard-menu-item ${activeMenuItem === item.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveMenuItem(item.id);
                  setMenuOpen(false);
                }}
              >
                <span className="dashboard-menu-item-icon"><Icon /></span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="dashboard-menu-footer">
          <button type="button" className="dashboard-menu-signout" onClick={logout}>
            <span className="dashboard-menu-item-icon"><MenuSignOutIcon /></span>
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
      <div className="dashboard-main">
        <div
          key={`${activeFlight ? `flight-${activeFlight.id}-${activeFlightMode}` : 'aircraft'}-${panelTransitionKey}`}
          className={`dashboard-view-panel dashboard-view-${panelDirection}`}
        >
          {activeFlight ? (
            activeFlightMode === 'post_flight' ? (
              <PostFlightMode
                flightId={activeFlight.id}
                onBack={handleBackToAircraftReport}
                onRevoke={handleReturnToOnGround}
              />
            ) : (
              <OnGroundMode
                flightId={activeFlight.id}
                onBack={handleBackToAircraftReport}
                onAccepted={handleMoveToPostFlight}
              />
            )
          ) : (
            <AircraftStatusReport
              aircraftId={aircraft?.id}
              onFlyAircraft={() => setShowFlyModal(true)}
            />
          )}
        </div>
      </div>

      {showFlyModal && (
        <FlyAircraftModal
          onClose={() => setShowFlyModal(false)}
          onSuccess={handleFlySuccess}
        />
      )}
    </div>
  );
};

export default DashboardPage;
